import { initCardExpiryInput, isCardExpiryValid } from './card-expiry';
import {
  initCardCvcInput,
  initCardNumberInput,
  isCardCvvValid,
  isCreditCardValid,
} from './card-validation';

function addFormieEwayValidators(validator) {
  if (!validator || validator._ewayValidatorsRegistered) return;

  validator._ewayValidatorsRegistered = true;

  validator.addValidator(
    'ewayCreditCard',
    ({ input }) => {
      if (input.getAttribute('data-eway-card') !== 'card-number') return true;
      if (!input.value.trim()) return true;

      return isCreditCardValid(input.value);
    },
    () => 'Please enter a valid card number'
  );

  validator.addValidator(
    'ewayCardCvc',
    ({ input }) => {
      if (input.getAttribute('data-eway-card') !== 'security-code') return true;
      if (!input.value.trim()) return true;

      return isCardCvvValid(input.value);
    },
    () => 'Please enter a valid CVV'
  );

  validator.addValidator(
    'ewayCardExpiry',
    ({ input }) => {
      if (input.getAttribute('data-eway-card') !== 'expiry-date') return true;
      if (!input.value.trim()) return true;

      return isCardExpiryValid(input.value);
    },
    () => 'Please enter a valid expiry date (MM/YY)'
  );
}

function hookExistingFormieForms() {
  document.querySelectorAll('form[data-formie-form]').forEach(($form) => {
    prepareFormieEwayPaymentFields($form);
    watchPaypalAuthInput($form);
  });
}

function prepareFormieEwayExpiryLabel(input) {
  const field = input.closest('.formie-field, [data-formie-field]');
  if (!field || field.dataset.expiryLabelFormatted === 'true') return;

  const label = field.querySelector('label');
  if (!label || label.textContent.includes('MM/YY')) return;

  const hint = document.createElement('span');
  hint.className = 'formie-label-hint';
  hint.textContent = ' MM/YY';

  const required = label.querySelector(
    '.formie-field-required, [data-formie-field-required]'
  );
  if (required) {
    label.insertBefore(hint, required);
  } else {
    label.appendChild(hint);
  }

  field.dataset.expiryLabelFormatted = 'true';
}

function attachFormieValidatorCleanup(input, form) {
  const validator = form?.formTheme?.validator;
  if (!validator || input.dataset.validatorCleanup === 'true') return;

  input.addEventListener('input', () => {
    validator.removeError(input);
  });

  input.dataset.validatorCleanup = 'true';
}

function prepareFormieEwayPaymentFields(root = document) {
  root.querySelectorAll('[data-eway-card="card-number"]').forEach((input) => {
    const form = input.closest('form');
    input.removeAttribute('placeholder');
    initCardNumberInput(input);
    attachFormieValidatorCleanup(input, form);
  });

  root.querySelectorAll('[data-eway-card="security-code"]').forEach((input) => {
    const form = input.closest('form');
    input.removeAttribute('placeholder');
    initCardCvcInput(input);
    attachFormieValidatorCleanup(input, form);
  });

  root.querySelectorAll('[data-eway-card="expiry-date"]').forEach((input) => {
    const form = input.closest('form');
    initCardExpiryInput(input, { placeholder: false });
    prepareFormieEwayExpiryLabel(input);
    attachFormieValidatorCleanup(input, form);
  });
}

function formFromEvent(event) {
  const target = event.target;

  if (target instanceof HTMLFormElement) return target;
  if (target instanceof Element) {
    return target.closest('form') || target.querySelector('form');
  }

  return null;
}

function initFormieEwayPayment(event) {
  addFormieEwayValidators(event.detail?.validator);
  const form = formFromEvent(event);
  if (form) {
    prepareFormieEwayPaymentFields(form);
    watchPaypalAuthInput(form);
  }
}

function isInvoicePaymentForm($form) {
  return Boolean($form?.closest?.('.invoice-payment-form'));
}

function getPaypalAuthInput($form) {
  return $form.querySelector('[name*="paypalAuthId"]');
}

function unlockPaypalAutoSubmit($form) {
  delete $form.dataset.paypalAutoSubmitting;
}

function submitInvoicePaymentForm($form) {
  const submitButton = $form.querySelector(
    '[type="submit"], .formie-button-submit'
  );

  if (typeof $form.requestSubmit === 'function') {
    $form.requestSubmit(submitButton || undefined);
    return;
  }

  if (submitButton) {
    submitButton.click();
    return;
  }

  $form.submit();
}

function autoSubmitInvoicePaypalForm($form) {
  if (!$form || $form.dataset.paypalAutoSubmitting === 'true') return;
  if (!getPaypalAuthInput($form)?.value) return;

  $form.dataset.paypalAutoSubmitting = 'true';
  $form.addEventListener(
    'formie:submit:result',
    (event) => {
      const result = event.detail?.result ?? event.detail;
      if (result?.ok === false) unlockPaypalAutoSubmit($form);
    },
    { once: true }
  );

  // Let Formie finish writing paypalAuthId before the same submit path as the button.
  window.setTimeout(() => {
    if (!getPaypalAuthInput($form)?.value) {
      unlockPaypalAutoSubmit($form);
      return;
    }

    submitInvoicePaymentForm($form);
  }, 50);
}

const watchedPaypalInputs = new WeakSet();

function watchPaypalAuthInput($form) {
  if (!isInvoicePaymentForm($form)) return;

  const input = getPaypalAuthInput($form);
  if (!input || watchedPaypalInputs.has(input)) return;

  watchedPaypalInputs.add(input);

  const notify = () => {
    if (input.value) autoSubmitInvoicePaypalForm($form);
  };

  input.addEventListener('input', notify);
  input.addEventListener('change', notify);

  // PayPal writes the auth id with a property set, which does not fire input.
  const descriptor = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  );

  if (!descriptor?.get || !descriptor?.set) return;

  let current = input.value;

  Object.defineProperty(input, 'value', {
    configurable: true,
    enumerable: descriptor.enumerable,
    get() {
      return descriptor.get.call(this);
    },
    set(next) {
      descriptor.set.call(this, next);
      if (next !== current) {
        current = next;
        notify();
      }
    },
  });
}

document.addEventListener('formie:validator:ready', initFormieEwayPayment);
document.addEventListener('formie:mount:after', initFormieEwayPayment);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hookExistingFormieForms);
} else {
  hookExistingFormieForms();
}
